import { useState, useEffect } from 'react';
import { analyticsAPI, messagesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler } from 'chart.js';
import { MessageSquare, Users, Bot, TrendingUp, Zap, Clock } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { Link } from 'react-router-dom';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

export default function OverviewPage() {
  const { merchant, user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    analyticsAPI.getOverview({ days: 30 })
      .then(({ data }) => setAnalytics(data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div>
      <div className="page-header"><div><h1 className="page-title">Overview</h1></div></div>
      <div className="page-body"><div className="flex-center" style={{ height: 200 }}><div className="spinner" /></div></div>
    </div>
  );

  const chartData = {
    labels: analytics?.messagesByDay?.map(d => format(new Date(d._id), 'MMM d')) || [],
    datasets: [
      {
        label: 'Inbound',
        data: analytics?.messagesByDay?.map(d => d.inbound) || [],
        borderColor: 'var(--primary)',
        backgroundColor: 'rgba(99,102,241,0.08)',
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointBackgroundColor: 'var(--primary)',
      },
      {
        label: 'Outbound',
        data: analytics?.messagesByDay?.map(d => d.outbound) || [],
        borderColor: 'var(--primary)',
        backgroundColor: 'rgba(99,102,241,0.06)',
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointBackgroundColor: 'var(--primary)',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: { legend: { display: false }, tooltip: { backgroundColor: 'var(--border)', titleColor: 'var(--text)', bodyColor: 'var(--text-muted)', borderColor: 'var(--border)', borderWidth: 1 } },
    scales: {
      x: { grid: { color: 'rgba(0,0,0,0.06)' }, ticks: { color: 'var(--text-muted)', font: { size: 11 } } },
      y: { grid: { color: 'rgba(0,0,0,0.06)' }, ticks: { color: 'var(--text-muted)', font: { size: 11 } } },
    },
  };

  const stats = analytics?.totals || {};

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Good {getGreeting()}, {user?.name?.split(' ')[0]}</h1>
          <p className="page-subtitle">Here's what's happening in {merchant?.businessName} today.</p>
        </div>
        <Link to="/dashboard/inbox" className="btn btn-primary">
          <MessageSquare size={15} /> Open Inbox
        </Link>
      </div>

      <div className="page-body">
        {/* Usage warning */}
        {merchant && getUsagePercent(merchant) > 80 && (
          <div className="alert alert-warning" style={{ marginBottom: 20 }}>
            <Zap size={16} />
            <span>You've used <strong>{getUsagePercent(merchant)}%</strong> of your monthly message quota.
              <Link to="/dashboard/billing" style={{ marginLeft: 6 }}>Upgrade plan →</Link>
            </span>
          </div>
        )}

        {/* Stats */}
        <div className="stats-grid" style={{ marginBottom: 20 }}>
          <StatCard icon={<MessageSquare size={18} />} iconBg="rgba(99,102,241,0.12)" iconColor="var(--primary)"
            label="Total Messages" value={stats.messages || 0} change="+12% this month" changeType="up" />
          <StatCard icon={<Users size={18} />} iconBg="rgba(99,102,241,0.12)" iconColor="var(--primary)"
            label="Active Customers" value={stats.activeCustomers || 0} change={`${stats.newCustomers || 0} new`} changeType="up" />
          <StatCard icon={<Bot size={18} />} iconBg="rgba(34,197,94,0.12)" iconColor="var(--green)"
            label="AI Replies" value={stats.aiReplies || 0} change={`${stats.messages ? Math.round((stats.aiReplies / stats.messages) * 100) : 0}% auto-replied`} changeType="up" />
          <StatCard icon={<Clock size={18} />} iconBg="rgba(249,115,22,0.12)" iconColor="var(--orange)"
            label="Avg AI Response" value={`${stats.avgAiResponseMs ? (stats.avgAiResponseMs / 1000).toFixed(1) : '—'}s`} change="Response time" changeType="neutral" />
        </div>

        {/* Chart + Top Customers */}
        <div className="grid-2" style={{ gap: 16 }}>
          <div className="card">
            <div className="card-header">
              <span className="card-title">Message Activity (30 days)</span>
              <div className="flex-gap" style={{ gap: 12 }}>
                <div className="flex-gap"><div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--primary)' }} /><span className="text-xs" style={{ color: 'var(--text-muted)' }}>Inbound</span></div>
                <div className="flex-gap"><div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--primary)' }} /><span className="text-xs" style={{ color: 'var(--text-muted)' }}>Outbound</span></div>
              </div>
            </div>
            {analytics?.messagesByDay?.length ? <Line data={chartData} options={chartOptions} /> : (
              <div className="empty-state" style={{ padding: '40px 0' }}>
                <TrendingUp size={32} /><h3>No data yet</h3><p>Data will appear once messages are received.</p>
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">Top Customers</span></div>
            {analytics?.topCustomers?.length ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {analytics.topCustomers.map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div className="avatar avatar-sm">{(item.customer?.name || '?')[0].toUpperCase()}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{item.customer?.name || 'Unknown'}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{item.count} messages</div>
                    </div>
                    <div style={{ fontSize: '0.72rem', background: 'var(--border)', padding: '2px 8px', borderRadius: 99, color: 'var(--text-muted)' }}>#{i + 1}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state" style={{ padding: '30px 0' }}><Users size={28} /><h3>No conversations yet</h3></div>
            )}
          </div>
        </div>

        {/* Usage bar */}
        {merchant && (
          <div className="card" style={{ marginTop: 16 }}>
            <div className="card-header">
              <span className="card-title">Plan Usage</span>
              <span className="badge badge-mauve">{merchant.subscription?.plan?.toUpperCase()}</span>
            </div>
            <UsageBar label="Messages" used={merchant.usage?.messagesThisMonth || 0} total={merchant.limits?.messagesPerMonth || 1000} color="var(--primary)" />
            <UsageBar label="AI Replies" used={merchant.usage?.aiRepliesThisMonth || 0} total={merchant.limits?.aiRepliesPerMonth || 500} color="var(--primary)" />
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, iconBg, iconColor, label, value, change, changeType }) {
  return (
    <div className="stat-card">
      <div className="stat-icon" style={{ background: iconBg, color: iconColor }}>{icon}</div>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      <div className={`stat-change ${changeType}`}>{change}</div>
    </div>
  );
}

function UsageBar({ label, used, total, color }) {
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  return (
    <div style={{ marginBottom: 14 }}>
      <div className="flex-between" style={{ marginBottom: 6 }}>
        <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{label}</span>
        <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{used.toLocaleString()} / {total === 999999 ? '∞' : total.toLocaleString()}</span>
      </div>
      <div style={{ height: 6, background: 'var(--border)', borderRadius: 99 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: pct > 80 ? 'var(--red)' : color, borderRadius: 99, transition: 'width 0.5s ease' }} />
      </div>
      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 3 }}>{pct}% used</div>
    </div>
  );
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

function getUsagePercent(merchant) {
  const used = merchant.usage?.messagesThisMonth || 0;
  const total = merchant.limits?.messagesPerMonth || 1000;
  return Math.round((used / total) * 100);
}
